import { useState, useEffect, useRef } from 'react'
import { motion, useScroll, useTransform, useInView } from 'framer-motion'
import { Award, Users, Globe, Target, CheckCircle, ArrowRight, ChevronLeft, ChevronRight, Building, Zap, Recycle } from 'lucide-react'
import '../styles/glassmorphism.css'

const AboutUs = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [currentSlide, setCurrentSlide] = useState(0)
  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const isInView = useInView(contentRef, { once: true, margin: "-100px" })

  const backgroundImages = [
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=2076&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070&auto=format&fit=crop"
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length)
    }, 6000)
    return () => clearInterval(interval)
  }, [])

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  })

  const values = [
    {
      icon: Target,
      title: "Excellence",
      description: "Committed to delivering the highest quality in every project"
    },
    {
      icon: Users,
      title: "Partnership",
      description: "Building lasting relationships with clients and communities"
    },
    {
      icon: Globe,
      title: "Innovation",
      description: "Embracing cutting-edge solutions for complex challenges"
    },
    {
      icon: Award,
      title: "Integrity",
      description: "Operating with transparency and ethical business practices"
    }
  ]

  const achievements = [
    "25+ Years of Industry Leadership",
    "150+ Successfully Completed Projects",
    "12 Countries Served Worldwide",
    "ISO 9001:2015 Quality Certified",
    "Environmental Compliance Excellence",
    "Local Community Development Focus"
  ]

  const slides = [
    {
      title: "Our Heritage",
      subtitle: "Building Since 1999",
      content: "Founded with a vision to transform Iraq's infrastructure landscape, Al-Konda has grown from a local construction company to a comprehensive solutions provider. Our journey spans over two decades of innovation, excellence, and unwavering commitment to quality.",
      icon: Building,
      stats: ["25+ Years", "Founded 1999", "Iraq & Kurdistan"]
    },
    {
      title: "Innovation & Technology",
      subtitle: "Leading the Future",
      content: "We embrace cutting-edge technologies and sustainable practices to deliver world-class solutions. From renewable energy systems to advanced construction techniques, we're at the forefront of industrial innovation in the Middle East.",
      icon: Zap,
      stats: ["Advanced Tech", "Sustainable Solutions", "Future Ready"]
    },
    {
      title: "Environmental Leadership",
      subtitle: "Sustainable Tomorrow",
      content: "Environmental stewardship is at the core of our operations. We're committed to reducing environmental impact through innovative recycling programs, clean energy solutions, and sustainable construction practices that benefit communities and ecosystems.",
      icon: Recycle,
      stats: ["Green Solutions", "Zero Waste Goal", "Clean Energy"]
    }
  ]

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }

  useEffect(() => {
    const slideInterval = setInterval(() => {
      nextSlide()
    }, 8000)
    return () => clearInterval(slideInterval)
  }, [])

  return (
    <section ref={containerRef} className="relative min-h-screen overflow-hidden">
      {/* Background Image Slider */}
      <div className="absolute inset-0">
        {backgroundImages.map((image, index) => (
          <motion.div
            key={index}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${image})` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentImageIndex ? 1 : 0 }}
            transition={{ duration: 2, ease: "easeInOut" }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      </div>

      {/* Glassmorphism floating elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          style={{ 
            y: useTransform(scrollYProgress, [0, 1], [0, -80]),
          }}
          className="absolute top-1/4 right-1/3 w-[200px] h-[200px] sm:w-[300px] sm:h-[300px] lg:w-[400px] lg:h-[400px] bg-white/10 backdrop-blur-xl rounded-full border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
        />
        <motion.div 
          style={{ 
            y: useTransform(scrollYProgress, [0, 1], [80, 0]),
          }}
          className="absolute bottom-1/4 left-1/4 w-[150px] h-[150px] sm:w-[200px] sm:h-[200px] lg:w-[300px] lg:h-[300px] bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
        />
        <motion.div 
          style={{ 
            y: useTransform(scrollYProgress, [0, 1], [40, -40]),
          }}
          className="absolute top-1/2 left-1/2 w-[100px] h-[100px] sm:w-[150px] sm:h-[150px] lg:w-[200px] lg:h-[200px] bg-white/15 backdrop-blur-xl rounded-full border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
        />
      </div>

      {/* Main Content */}
      <div className="relative z-20 min-h-screen flex items-center py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          {/* Main glassmorphism container */}
          <div ref={contentRef} className="bg-white/5 backdrop-blur-2xl rounded-xl sm:rounded-2xl lg:rounded-[32px] border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.2)] p-4 sm:p-6 md:p-8 lg:p-12 xl:p-20 w-full max-w-[95vw] mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8 }}
              className="space-y-8 lg:space-y-12"
            >
              {/* Header */}
              <div className="text-center space-y-4 sm:space-y-6">
                <div className="text-white/70 text-xs sm:text-sm font-medium tracking-[0.2em] uppercase">
                  About Al-Konda
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light text-white leading-[0.9] tracking-tight">
                  Building Excellence
                  <br />
                  <span className="text-white/80">Since 1999</span>
                </h1>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/80 font-light leading-relaxed max-w-4xl mx-auto">
                  Al-Konda is a leading Iraqi company specializing in construction, engineering, environmental services, and sustainable solutions across the Middle East and beyond.
                </p>
              </div>

              {/* Interactive Content Slider */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="bg-white/10 backdrop-blur-xl rounded-xl p-6 lg:p-8 border border-white/20 relative overflow-hidden"
              >
                {/* Slider Content */}
                <div className="relative min-h-[400px] lg:min-h-[300px]">
                  {slides.map((slide, index) => (
                    <motion.div
                      key={index}
                      className="absolute inset-0 grid lg:grid-cols-2 gap-6 lg:gap-8 items-center"
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ 
                        opacity: index === currentSlide ? 1 : 0,
                        x: index === currentSlide ? 0 : index < currentSlide ? -100 : 100
                      }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                    >
                      {/* Content */}
                      <div className="space-y-4 lg:space-y-6">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                            <slide.icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl lg:text-2xl font-light text-white">{slide.title}</h3>
                            <p className="text-white/60 text-sm">{slide.subtitle}</p>
                          </div>
                        </div>
                        <p className="text-white/70 font-light leading-relaxed text-sm lg:text-base">
                          {slide.content}
                        </p>
                        <div className="flex gap-4">
                          {slide.stats.map((stat, idx) => (
                            <div key={idx} className="bg-white/10 rounded-lg px-3 py-2">
                              <span className="text-white/80 text-xs lg:text-sm font-medium">{stat}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Visual Element */}
                      <div className="hidden lg:flex items-center justify-center">
                        <div className="w-48 h-48 bg-white/5 backdrop-blur-xl rounded-full border border-white/20 flex items-center justify-center">
                          <slide.icon className="w-20 h-20 text-white/40" />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Slider Controls */}
                <div className="flex items-center justify-between mt-6">
                  <button
                    onClick={prevSlide}
                    className="w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  
                  <div className="flex gap-2">
                    {slides.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index === currentSlide ? 'bg-white w-8' : 'bg-white/30 w-2 hover:bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                  
                  <button
                    onClick={nextSlide}
                    className="w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                </div>
              </motion.div>

              {/* Mission & Vision Grid */}
              <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="bg-white/10 backdrop-blur-xl rounded-xl p-6 lg:p-8 border border-white/20"
                >
                  <h3 className="text-xl lg:text-2xl font-light text-white mb-4">Our Mission</h3>
                  <p className="text-white/70 font-light leading-relaxed">
                    To provide innovative, sustainable, and high-quality solutions that drive economic 
                    growth, environmental stewardship, and social development in the communities we serve.
                  </p>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
                  transition={{ delay: 0.7, duration: 0.8 }}
                  className="bg-white/10 backdrop-blur-xl rounded-xl p-6 lg:p-8 border border-white/20"
                >
                  <h3 className="text-xl lg:text-2xl font-light text-white mb-4">Our Vision</h3>
                  <p className="text-white/70 font-light leading-relaxed">
                    To be the leading integrated solutions provider in the Middle East, recognized for 
                    excellence, innovation, and commitment to sustainable development.
                  </p>
                </motion.div>
              </div>

              {/* Values Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="space-y-6"
              >
                <h2 className="text-2xl lg:text-3xl font-light text-white text-center">
                  Our Core Values
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                  {values.map((value, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: 1.1 + idx * 0.1, duration: 0.6 }}
                      className="bg-white/10 backdrop-blur-xl rounded-xl p-4 lg:p-6 group hover:scale-105 transition-transform duration-300 border border-white/20 text-center"
                    >
                      <div className="w-10 h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg flex items-center justify-center mb-3 lg:mb-4 mx-auto group-hover:scale-110 transition-transform duration-300">
                        <value.icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                      </div>
                      <h3 className="text-sm lg:text-lg font-medium text-white mb-2">{value.title}</h3>
                      <p className="text-white/60 text-xs lg:text-sm font-light">{value.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Achievements */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ delay: 1.3, duration: 0.8 }}
                className="bg-white/10 backdrop-blur-xl rounded-xl p-6 lg:p-8 border border-white/20"
              >
                <h2 className="text-2xl lg:text-3xl font-light text-white mb-6 text-center">
                  Key Achievements
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {achievements.map((achievement, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                      transition={{ delay: 1.5 + idx * 0.05, duration: 0.5 }}
                      className="flex items-center text-white/80"
                    >
                      <CheckCircle className="w-4 h-4 lg:w-5 lg:h-5 text-green-400 mr-3 flex-shrink-0" />
                      <span className="font-light text-sm lg:text-base">{achievement}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* CTA Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ delay: 1.7, duration: 0.8 }}
                className="text-center pt-6"
              >
                <button className="group bg-white text-black px-6 py-3 sm:px-8 sm:py-4 rounded-full font-medium hover:bg-white/90 transition-all duration-300 flex items-center gap-2 sm:gap-3 justify-center text-sm sm:text-base mx-auto">
                  Partner With Us
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Progress Indicators */}
      <div className="absolute bottom-4 sm:bottom-6 lg:bottom-8 left-4 sm:left-6 lg:left-8 z-30 flex gap-1 sm:gap-2">
        {backgroundImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`h-1 rounded-full transition-all duration-300 ${
              index === currentImageIndex ? 'bg-white w-8 sm:w-10 lg:w-12' : 'bg-white/30 w-4 sm:w-5 lg:w-6 hover:bg-white/50'
            }`}
          />
        ))}
      </div>
    </section>
  )
}

export default AboutUs